import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Shield, Users, Calendar, Clock, MapPin, 
  User, Mail, Target, Video, ClipboardList, ChevronRight,
  TrendingUp, Award, Star, CheckCircle, Play, FileText, MessageSquare
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isThisWeek } from 'date-fns';
import PlayerDevelopmentProfile from '@/components/PlayerDevelopmentProfile';
import DevelopmentRadarChart from '@/components/DevelopmentRadarChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Skills organized by category
const SKILL_CATEGORIES = [
  {
    category: 'TECHNICAL',
    color: 'text-emerald-400',
    skills: [
      { key: 'ball_control', label: 'Ball Control' },
      { key: 'passing', label: 'Passing' },
      { key: 'dribbling', label: 'Dribbling' },
      { key: 'first_touch', label: 'First Touch' },
      { key: 'finishing', label: 'Finishing' },
      { key: 'shooting', label: 'Shooting' },
    ]
  },
  {
    category: 'PHYSICAL',
    color: 'text-blue-400',
    skills: [
      { key: 'speed', label: 'Speed' },
      { key: 'acceleration', label: 'Acceleration' },
      { key: 'agility', label: 'Agility' },
      { key: 'coordination', label: 'Coordination' },
      { key: 'balance', label: 'Balance' },
      { key: 'explosiveness', label: 'Explosiveness' },
      { key: 'endurance', label: 'Endurance' },
    ]
  },
  {
    category: 'TACTICAL / IQ',
    color: 'text-yellow-400',
    skills: [
      { key: 'positioning', label: 'Positioning' },
      { key: 'decision_making', label: 'Decision Making' },
      { key: 'vision', label: 'Vision' },
      { key: 'game_awareness', label: 'Game Awareness' },
      { key: 'reaction_time', label: 'Reaction Time' },
    ]
  }
];

// Flatten skills for easy access
const ALL_SKILLS = SKILL_CATEGORIES.flatMap(cat => cat.skills);

const ParentTeamView = ({ section }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();
  
  const [teamInfo, setTeamInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [techDev, setTechDev] = useState(null);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [videos, setVideos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(section || 'overview');

  // Update activeTab when section prop changes
  useEffect(() => {
    if (section) {
      setActiveTab(section);
    }
  }, [section]);

  useEffect(() => {
    fetchTeamData();
  }, [teamId, athlete]);

  const fetchTeamData = async () => {
    if (!athlete) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch team info from parent endpoint
      const teamResponse = await fetch(`${API_URL}/api/parent/team/${teamId}`, {
        headers: getAuthHeaders()
      });
      
      if (!teamResponse.ok) {
        if (teamResponse.status === 403) {
          setError('You do not have access to this team');
        } else if (teamResponse.status === 404) {
          setError('Team not found');
        } else {
          setError('Failed to load team information');
        }
        return;
      }
      
      const teamData = await teamResponse.json();
      setTeamInfo(teamData.team);
      setSessions(teamData.upcoming_sessions || []);
      
      // Fetch technical development for this athlete in this team
      try {
        const techRes = await fetch(
          `${API_URL}/api/parent/team/${teamId}/athlete/${athlete.id}/technical-development`,
          { headers: getAuthHeaders() }
        );
        if (techRes.ok) {
          const techData = await techRes.json();
          setTechDev(techData);
        }
      } catch (e) {
        console.log('No technical development data');
      }
      
      // Fetch training plans for this team
      try {
        const plansRes = await fetch(
          `${API_URL}/api/parent/team/${teamId}/training-plans`,
          { headers: getAuthHeaders() }
        );
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setTrainingPlans(plansData);
        }
      } catch (e) {
        console.log('No training plans data');
      }
      
      // Fetch videos for this team
      try {
        const videosRes = await fetch(
          `${API_URL}/api/parent/team/${teamId}/videos`,
          { headers: getAuthHeaders() }
        );
        if (videosRes.ok) {
          const videosData = await videosRes.json();
          setVideos(videosData);
        }
      } catch (e) {
        console.log('No videos data');
      }
      
      // Fetch team messages
      try {
        const messagesRes = await fetch(
          `${API_URL}/api/parent/team/${teamId}/messages`,
          { headers: getAuthHeaders() }
        );
        if (messagesRes.ok) {
          const messagesData = await messagesRes.json();
          setMessages(messagesData);
        }
      } catch (e) {
        console.log('No messages data');
      }
      
    } catch (err) {
      console.error('Failed to fetch team data:', err);
      setError('Failed to load team information');
    } finally {
      setLoading(false);
    }
  };

  const getSessionDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isThisWeek(date)) return format(date, 'EEEE');
    return format(date, 'MMM d');
  };

  const getSessionTypeColor = (type) => {
    switch (type) {
      case 'game': return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'tournament': return 'bg-purple-500/10 text-purple-400 border-purple-500/30';
      case 'meeting': return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      default: return 'bg-green-500/10 text-green-400 border-green-500/30';
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toString().toLowerCase();
    if (s === 'proficient') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (s === 'developing') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (s === 'focus') return 'bg-red-500/20 text-red-400 border-red-500/30';
    return 'bg-muted text-muted-foreground';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'technical', label: 'Technical Dev', icon: Target },
    { id: 'training', label: 'Training Plans', icon: ClipboardList },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="team-view-loading">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12" data-testid="team-view-error">
        <Shield className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl uppercase mb-2">Access Error</h2>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => navigate('/dashboard')} variant="outline">
          <ArrowLeft size={16} className="mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  if (!teamInfo) {
    return null;
  }

  // Calculate development summary
  const devSummary = techDev ? {
    focus: Object.values(techDev).filter(s => s?.status === 'Focus').length,
    developing: Object.values(techDev).filter(s => s?.status === 'Developing').length,
    proficient: Object.values(techDev).filter(s => s?.status === 'Proficient').length
  } : { focus: 0, developing: 0, proficient: 0 };

  return (
    <div className="space-y-6" data-testid="parent-team-view">
      {/* Back Button & Context Indicator */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-to-private-coach-btn"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Private Coach Context
        </Button>
        
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-sm">
          <Shield size={14} className="text-blue-400" />
          <span className="text-xs text-blue-400 uppercase tracking-wider font-medium">Team Context</span>
        </div>
      </div>

      {/* Team Header with Athlete Info */}
      <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/30 rounded-sm p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
          {/* Team Badge */}
          <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Shield className="text-blue-400" size={40} />
          </div>
          
          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl">{teamInfo.name}</h1>
            <p className="text-muted-foreground mt-1">
              {teamInfo.club_name} • {teamInfo.category}
            </p>
            
            {/* Athlete in Team */}
            {athlete && (
              <div className="mt-3 p-3 bg-card/50 border border-border/40 rounded-sm inline-block">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Viewing as</p>
                <p className="font-medium text-blue-400">{athlete.name}</p>
              </div>
            )}
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-4 mt-4">
              <div className="flex items-center gap-2 text-sm">
                <Users size={16} className="text-muted-foreground" />
                <span>{teamInfo.athletes_count || 0} Athletes</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar size={16} className="text-muted-foreground" />
                <span>{sessions.length} Upcoming Sessions</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <ClipboardList size={16} className="text-muted-foreground" />
                <span>{trainingPlans.length} Training Plans</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-blue-500 text-white' 
                : 'bg-card border border-border hover:bg-muted hover:border-blue-500/30'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border/40 rounded-sm p-6">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Development Summary */}
            <div>
              <h2 className="font-heading text-xl uppercase mb-4">Development Summary</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{devSummary.focus}</p>
                  <p className="text-sm text-red-400">Focus</p>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{devSummary.developing}</p>
                  <p className="text-sm text-yellow-400">Developing</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{devSummary.proficient}</p>
                  <p className="text-sm text-green-400">Proficient</p>
                </div>
              </div>
            </div>

            {/* Coaches Section */}
            {teamInfo.coaches && teamInfo.coaches.length > 0 && (
              <div>
                <h2 className="font-heading text-xl uppercase mb-4">Team Coaches</h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {teamInfo.coaches.map((coach, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-sm border ${
                        coach.role === 'head_coach' 
                          ? 'bg-blue-500/10 border-blue-500/30' 
                          : 'bg-muted/30 border-border'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          coach.role === 'head_coach' ? 'bg-blue-500/20' : 'bg-muted'
                        }`}>
                          <User size={18} className={coach.role === 'head_coach' ? 'text-blue-400' : 'text-muted-foreground'} />
                        </div>
                        <div>
                          <p className="font-medium">{coach.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {coach.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}
                          </p>
                        </div>
                      </div>
                      {coach.email && (
                        <a 
                          href={`mailto:${coach.email}`}
                          className="flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                        >
                          <Mail size={12} />
                          {coach.email}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div>
              <h2 className="font-heading text-xl uppercase mb-4">Quick Access</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {tabs.filter(t => t.id !== 'overview').map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex flex-col items-center gap-2 p-4 bg-muted/30 border border-border hover:border-blue-500/50 rounded-sm transition-colors"
                  >
                    <tab.icon size={24} className="text-blue-400" />
                    <span className="text-sm">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Technical Development Tab */}
        {activeTab === 'technical' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Technical Development</h2>
              <span className="text-xs text-muted-foreground">Updated by team coaches</span>
            </div>
            
            {!techDev || Object.keys(techDev).length === 0 ? (
              <div className="text-center py-12">
                <Target className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg mb-2">No Development Data Yet</h3>
                <p className="text-muted-foreground text-sm">
                  Your team coach hasn't added technical development data yet.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Radar Chart - Development Visualization */}
                <DevelopmentRadarChart techDev={techDev} />

                {/* Player Development Profile Summary */}
                <PlayerDevelopmentProfile techDev={techDev} />

                {/* Status Legend */}
                <div className="bg-muted/30 p-3 rounded-sm">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Focus</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Developing</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Proficient</span>
                  </div>
                </div>

                {SKILL_CATEGORIES.map((category) => (
                  <div key={category.category}>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className={`font-heading text-lg uppercase tracking-wider ${category.color}`}>
                        {category.category}
                      </h3>
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                    <div className="space-y-3 mb-6">
                      {category.skills.map((skill) => {
                        const data = techDev[skill.key];
                        if (!data) return null;
                        
                        return (
                          <div key={skill.key} className="bg-muted/30 border border-border/40 p-4 rounded-sm">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <Target className="text-blue-400" size={18} />
                                <h4 className="font-semibold">{skill.label}</h4>
                              </div>
                              <span className={`px-3 py-1 rounded border text-sm ${getStatusColor(data.status)}`}>
                                {data.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase() : 'Not Assessed'}
                              </span>
                            </div>
                            {data.notes && (
                              <p className="text-sm text-muted-foreground bg-card/50 p-3 rounded-sm mt-2">
                                <span className="text-blue-400 font-medium">Coach Notes:</span> {data.notes}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Training Plans Tab */}
        {activeTab === 'training' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Training Plans</h2>
            
            {trainingPlans.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg mb-2">No Training Plans</h3>
                <p className="text-muted-foreground text-sm">
                  No training plans have been created for this team yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {trainingPlans.map((plan) => (
                  <div key={plan.id} className="bg-muted/30 border border-border/40 p-4 rounded-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{plan.name}</h3>
                        <p className="text-sm text-muted-foreground capitalize">{plan.plan_type} Plan</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        plan.athlete_id ? 'bg-blue-500/20 text-blue-400' : 'bg-muted text-muted-foreground'
                      }`}>
                        {plan.athlete_id ? 'Individual' : 'Team-wide'}
                      </span>
                    </div>
                    
                    {plan.objectives?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-muted-foreground uppercase mb-2">Objectives:</p>
                        <ul className="space-y-1">
                          {plan.objectives.map((obj, idx) => (
                            <li key={idx} className="text-sm flex items-center gap-2">
                              <CheckCircle size={12} className="text-blue-400" />
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {plan.drill_ids?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {plan.drill_ids.length} drill(s) assigned
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Video Analysis</h2>
            
            {videos.length === 0 ? (
              <div className="text-center py-12">
                <Video className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg mb-2">No Videos</h3>
                <p className="text-muted-foreground text-sm">
                  No videos have been added for this team yet.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {videos.map((video) => (
                  <div key={video.id} className="bg-muted/30 border border-border/40 p-4 rounded-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium">{video.title}</h3>
                        <p className="text-sm text-muted-foreground capitalize">
                          {video.video_type?.replace('_', ' ')}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        video.athlete_id ? 'bg-blue-500/20 text-blue-400' : 'bg-muted'
                      }`}>
                        {video.athlete_id ? 'Individual' : 'Team'}
                      </span>
                    </div>
                    
                    {video.description && (
                      <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed line-clamp-6 mb-3">{video.description}</p>
                    )}
                    
                    <a 
                      href={video.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-2 text-blue-400 text-sm hover:underline"
                    >
                      <Play size={14} />
                      Watch Video
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Team Messages</h2>
            
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg mb-2">No Messages</h3>
                <p className="text-muted-foreground text-sm">
                  No messages have been sent to this team yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div 
                    key={message.id}
                    className="bg-muted/30 border border-border/40 p-4 rounded-sm"
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{message.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <User size={12} />
                          <span>{message.sent_by_name}</span>
                          <span>•</span>
                          <span>{format(parseISO(message.created_at), 'MMM d, yyyy HH:mm')}</span>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        message.message_type === 'urgent' 
                          ? 'bg-red-500/20 text-red-400' 
                          : message.message_type === 'announcement'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-muted text-muted-foreground'
                      }`}>
                        {message.message_type?.charAt(0).toUpperCase() + message.message_type?.slice(1) || 'General'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {message.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Upcoming Sessions</h2>
            
            {sessions.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg mb-2">No Upcoming Sessions</h3>
                <p className="text-muted-foreground text-sm">
                  No sessions have been scheduled for this team yet.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div 
                    key={session.id}
                    className="flex items-center gap-4 p-4 bg-muted/30 rounded-sm border border-border hover:border-blue-500/30 transition-colors"
                    data-testid={`session-${session.id}`}
                  >
                    {/* Date Badge */}
                    <div className="flex flex-col items-center justify-center w-16 h-16 bg-blue-500/10 rounded-sm flex-shrink-0">
                      <span className="text-xs text-blue-400 uppercase">
                        {format(parseISO(session.date), 'MMM')}
                      </span>
                      <span className="text-2xl font-bold text-blue-400">
                        {format(parseISO(session.date), 'd')}
                      </span>
                    </div>
                    
                    {/* Session Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getSessionTypeColor(session.session_type)}`}>
                          {session.session_type?.charAt(0).toUpperCase() + session.session_type?.slice(1)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {getSessionDateLabel(session.date)}
                        </span>
                      </div>
                      <p className="font-medium truncate">{session.title}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {format(parseISO(session.date), 'HH:mm')}
                        </span>
                        {session.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} />
                            {session.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentTeamView;
